import type { FunctionComponent } from 'react'
import React, { useState } from 'react'
import type { BadgeTheme } from '@which/seatbelt'
import {
  AnimationWrapper,
  AnnualRunningCost,
  Badge,
  ButtonLink,
  ColourSwatchGroup,
  Link,
  ProductScoreGauge,
  SaveButton,
  TypographyV2 as Typography,
} from '@which/seatbelt'
import { ProductCardCompareCheckbox } from '@which/seatbelt/src/components/Cards/ProductCards/shared/ProductCardCompareCheckbox/ProductCardCompareCheckbox'
import { SolidPadlockIcon } from '@which/seatbelt/src/components/Icons'
import {
  AMAZON_AFFILIATE_TAG,
  dynamicDatalayerPush,
  getSingularCategoryName,
  getTaggedAmazonUrl,
} from '@which/shared'

import { useFeatureIsOn } from '@growthbook/growthbook-react'
import classnames from 'classnames'

import type { ProductOffer } from '../../../generated/frontend'
import { ArticleTrackonomicsLink } from '../../../pages/article/components'
import { ProductBadges } from '../../../pages/reviews/shared/components/ProductBadges/ProductBadges'
import { addHandlerToRunningCostTooltip } from '../../../pages/reviews/utils/add-handler-to-running-cost-tooltip'
import { convertPriceValueToFloat } from '../../../pages/reviews/utils/convert-price-value-to-float'
import { mapAnnualRunningCost } from '../../../pages/reviews/utils/map-annual-running-cost'
import { useSubscriptionCostInfo } from '../../hooks/useSubscriptionCostInfo'
import { formatRetailers } from '../../utils/formatRetailers'
import { SkipButton } from '../SkipButton'
import {
  getSources,
  runningCostTooltipHandler,
  selectProductImage,
  type TableItemProps,
} from './CardedDynamicTable'
import styles from './CardedDynamicTable.module.scss'
import { LoggedOutImage } from './LoggedOutImage'
import { TopRated } from './TopRated/TopRated'

export const CardedDynamicTableItem: FunctionComponent<TableItemProps> = ({
  addProductHandler,
  annualRunningCost,
  availableOffersCount = 0,
  badgeNames = [],
  businessKey,
  categorySlug,
  description,
  dynamicTableId,
  image = { images: [] },
  index,
  isAllowCompare,
  isComparisonLimitReached,
  isSelectedForComparison,
  key,
  keyTestResults = [],
  logInCTA,
  loginStatus,
  manufacturer,
  model,
  offers = [],
  onSaveClick,
  price,
  productScoreGauge,
  removeProductHandler,
  saved,
  signUpCTA,
  slug,
  topRated,
  trackingPrefix,
  variants,
}) => {
  const [selectedVariant, setSelectedVariant] = useState(variants?.[0])
  const hasSafetyAlert = badgeNames.find((badge) => badge === 'safety alert')
  const filteredBadges = badgeNames.filter((badge) => badge !== 'safety alert')
  const manufacturerName = manufacturer?.name
  const validOffers = formatRetailers(offers)
  const singularCategoryName = getSingularCategoryName({
    categoryName: categorySlug?.replace(/-/g, ' '),
  })
  const subscriptionCostInfo = useSubscriptionCostInfo()

  const formattedRunningCost =
    annualRunningCost &&
    addHandlerToRunningCostTooltip(mapAnnualRunningCost({ annualRunningCost, loginStatus }), () =>
      runningCostTooltipHandler(trackingPrefix)
    )

  const handleAffiliateClick = (validOffer: ProductOffer) => {
    const item_spec = new URL(validOffer.url).hostname

    dynamicDatalayerPush({
      eventCategory: 'Where to Buy',
      eventAction: 'Go to Retailer',
      eventLabel: `${
        validOffer.retailer.name
      } | ${manufacturerName} ${model} | ${convertPriceValueToFloat(validOffer.priceValue, true)}`,
      eventValue: Math.floor(Number(convertPriceValueToFloat(validOffer.priceValue))),
      item_url: validOffer.url,
      item_spec,
      item_group: trackingPrefix === 'spp' ? 'smart product picker' : 'dynamic table',
    })

    if (topRated) {
      dynamicDatalayerPush({
        eventCategory: 'ab test',
        eventAction: 'WHI302',
        eventLabel: 'Top card - where to buy clicks',
      })
    }
  }

  const addToCompare = () => {
    addProductHandler?.({
      manufacturer: manufacturerName ?? '',
      model: model ?? '',
      businessKey: key ?? '',
      slug: slug ?? '',
    })
  }

  const removeFromCompare = () => {
    removeProductHandler?.({
      manufacturer: manufacturerName ?? '',
      model: model ?? '',
      businessKey: key ?? '',
    })
  }

  const linkStyleFeatureEnabled = useFeatureIsOn('pie-588-retailer-link-styling')

  return (
    <li className={styles.listItem} data-testid="cdt-card" key={`${dynamicTableId}-${index}`}>
      <article className={classnames(styles.card, topRated && 'topRatedVariants2366')}>
        {/* Top rated variants; https://whichonline.atlassian.net/browse/RS-2366 */}
        {topRated && <TopRated />}
        {topRated && <TopRated link={`/reviews/${categorySlug}`} />}
        <div className={styles.wrapper}>
          {filteredBadges && (
            <div data-testid="badges">
              <ProductBadges badges={filteredBadges} position="left" className={styles.badges} />
            </div>
          )}
          <div className={styles.summary}>
            <div className={classnames(styles.imageContainer, styles.summaryImage)}>
              {hasSafetyAlert && (
                <Badge theme={'safety alert' as BadgeTheme} className={styles.safetyAlert} />
              )}
              {image?.images?.length ? (
                <picture>
                  {getSources(image.images)?.map(({ srcset, type }, imageIndex) => (
                    <source key={`image-source-${imageIndex + 1}`} srcSet={srcset} type={type} />
                  ))}
                  <img
                    alt={`${manufacturerName} ${model}`}
                    className={styles.productImage}
                    src={selectProductImage(image.images)}
                    data-testid="product-image"
                  />
                </picture>
              ) : (
                <LoggedOutImage
                  className={styles.productImage}
                  data-testid="logged-out-image"
                  data-which-id={`${trackingPrefix}-product-image-signup`}
                />
              )}
              {isAllowCompare && (
                <ProductCardCompareCheckbox
                  businessKey=""
                  className={classnames(styles.compareCheckbox)}
                  comparisonLimitReached={isComparisonLimitReached}
                  handleAddCompare={addToCompare}
                  handleRemoveCompare={removeFromCompare}
                  isSelectedForComparison={isSelectedForComparison}
                  manufacturer={manufacturerName ?? ''}
                  model={model ?? ''}
                  slug={slug ?? ''}
                  type="all-breakpoints"
                />
              )}
            </div>
            <div className={styles.saveHeader}>
              <div className={styles.summaryDetails}>
                {model ? (
                  <div className={styles.section} data-testid="product-title">
                    <Typography tag="h3" textStyle="sb-text-heading-small">
                      {manufacturerName}
                    </Typography>
                    <Typography
                      className={styles.productTitle}
                      textStyle="sb-text-body-default-regular"
                    >
                      {model}
                    </Typography>
                  </div>
                ) : (
                  <div className={styles.section}>
                    <div className={styles.hiddenManufacturer} />
                    <div className={styles.hiddenModel} />
                  </div>
                )}
                {variants && selectedVariant && (
                  <div className={styles.colourSwatch}>
                    <ColourSwatchGroup
                      classNameRadioGroup={styles.radioGroup}
                      name={`colour-swatch-group-${businessKey}`}
                      onChange={setSelectedVariant}
                      selectedVariant={selectedVariant}
                      variants={variants}
                    />
                  </div>
                )}
                <div className={styles.section}>
                  {price && availableOffersCount === 0 && (
                    <>
                      <Typography
                        className={styles.price}
                        tag="span"
                        textStyle="sb-text-heading-small"
                      >
                        {price}
                      </Typography>
                      {availableOffersCount === 0 && (
                        <Typography
                          className={styles.typicalPriceLabel}
                          tag="span"
                          textStyle="sb-text-body-small-regular"
                        >
                          Typical price
                        </Typography>
                      )}
                    </>
                  )}
                  {availableOffersCount > 0 && (
                    <ol
                      className={styles.offersList}
                      data-testid="cheapest-available-retailers"
                      data-which-id="cheapest-available-retailers"
                    >
                      {validOffers.map((validOffer) => {
                        const { retailer, url, isTrackable, formattedPrice } = validOffer
                        const textStyle = 'sb-text-interface-body-small-strong'

                        return (
                          <li key={retailer.name} className={styles.offersListItem}>
                            {isTrackable ? (
                              <Typography tag="span" textStyle={textStyle}>
                                {formattedPrice}
                                <ArticleTrackonomicsLink
                                  className={classnames(styles.leftMargin, {
                                    [styles.linkStyleFeatureEnabled]: linkStyleFeatureEnabled,
                                  })}
                                  contentType={'article'}
                                  href={url}
                                  optionalTracking={{
                                    item_group:
                                      trackingPrefix === 'spp'
                                        ? 'smart product picker'
                                        : 'dynamic table',
                                  }}
                                  onClick={() => handleAffiliateClick(validOffer)}
                                >
                                  {linkStyleFeatureEnabled ? (
                                    <Typography tag="span" textStyle={textStyle}>
                                      {retailer.name}
                                    </Typography>
                                  ) : (
                                    <Typography tag="span" textStyle={textStyle}>
                                      <AnimationWrapper>{retailer.name}</AnimationWrapper>
                                    </Typography>
                                  )}
                                </ArticleTrackonomicsLink>
                              </Typography>
                            ) : (
                              <Typography tag="span" textStyle={textStyle}>
                                {formattedPrice}
                                <Link
                                  aria-label={`Buy from ${retailer.name} at ${formattedPrice}`}
                                  className={classnames(styles.link, styles.leftMargin, {
                                    [styles.linkStyleFeatureEnabled]: linkStyleFeatureEnabled,
                                  })}
                                  data-which-id="affiliate-link"
                                  href={getTaggedAmazonUrl(url, AMAZON_AFFILIATE_TAG)}
                                  onClick={() => handleAffiliateClick(validOffer)}
                                  rel="nofollow noopener noreferrer"
                                  target="_blank"
                                >
                                  {linkStyleFeatureEnabled ? (
                                    <Typography tag="span" textStyle={textStyle}>
                                      {retailer.name}
                                    </Typography>
                                  ) : (
                                    <Typography tag="span" textStyle={textStyle}>
                                      <AnimationWrapper>{retailer.name}</AnimationWrapper>
                                    </Typography>
                                  )}
                                </Link>
                              </Typography>
                            )}
                          </li>
                        )
                      })}
                    </ol>
                  )}
                </div>
                {keyTestResults.length > 0 && (
                  <>
                    <ul className={styles.keyTestResultsList} data-testid="key-test-results">
                      {keyTestResults.map(({ label }) => (
                        <li key={label} className={styles.keyTestResultsListItem}>
                          <SolidPadlockIcon
                            className={styles.padlockIcon}
                            data-testid="padlock-icon"
                            height={15}
                            viewBox="0 0 15 15"
                            width={15}
                          />
                          <Typography tag="span" textStyle="sb-text-body-default-regular">
                            {label}
                          </Typography>
                        </li>
                      ))}
                    </ul>
                    <Link
                      className={classnames(styles.listingLink, {
                        [`${styles.linkStyleFeatureEnabled} ${styles.large}`]:
                          linkStyleFeatureEnabled,
                      })}
                      data-which-id="productcard-all-reviews"
                      href={`/reviews/${categorySlug}`}
                    >
                      {`All ${singularCategoryName} reviews`}
                    </Link>
                  </>
                )}
              </div>
              <SaveButton
                onClick={onSaveClick}
                productName={`${manufacturer} ${model}`}
                selected={saved}
              />
            </div>
            {productScoreGauge && (
              <ProductScoreGauge
                {...productScoreGauge}
                className={classnames(styles.section, styles.scoreAndBadges, styles.noMargin)}
              />
            )}
          </div>
          {description && (
            <Typography
              className={styles.description}
              data-testid="description"
              textStyle="sb-text-body-default-regular"
            >
              {description}
            </Typography>
          )}
          <ButtonLink
            appearance="secondary"
            className={styles.signuplink}
            href={signUpCTA?.url || `/reviews/${categorySlug}/${slug}#review`}
            data-test-element="review-button"
            data-testid="sign-up-button"
            data-tracking-element="review-button"
            data-which-id={
              signUpCTA?.url
                ? `${trackingPrefix}-product-signup`
                : `${trackingPrefix}-review-button`
            }
          >
            {signUpCTA?.label || 'Read full review'}
          </ButtonLink>
          {image?.images?.length && (
            <Link
              className={classnames(styles.memberListingLink, {
                [`${styles.linkStyleFeatureEnabled} ${styles.large}`]: linkStyleFeatureEnabled,
              })}
              data-which-id="productcard-all-reviews"
              href={`/reviews/${categorySlug}`}
            >
              {`All ${singularCategoryName} reviews`}
            </Link>
          )}
          {signUpCTA && subscriptionCostInfo?.CTACaptionText && (
            <div data-testid="signUpCTA-smallprint">
              <Typography textStyle="sb-text-body-small-regular">
                {subscriptionCostInfo.CTACaptionText}
              </Typography>
            </div>
          )}
          {logInCTA && (
            <div className={styles.loginWrapper}>
              <Typography
                data-testid="login-cta"
                tag="span"
                textStyle="sb-text-interface-body-small-regular"
              >
                {logInCTA?.copy}
                <Link
                  className={classnames(styles.loginLink, {
                    [`${styles.linkStyleFeatureEnabled} ${styles.large}`]: linkStyleFeatureEnabled,
                  })}
                  data-test-element="description-log-in"
                  data-which-id={`${trackingPrefix}-member-log-in-link`}
                  href={logInCTA.url}
                >
                  {logInCTA.label}
                </Link>
              </Typography>
            </div>
          )}
        </div>
        {formattedRunningCost && <AnnualRunningCost {...formattedRunningCost} />}
      </article>
      {isAllowCompare && isSelectedForComparison && (
        <SkipButton
          className={styles.skipToCompareButton}
          selector='button[data-testid="reviews-compare-button"]'
        >
          Skip to Compare Tray
        </SkipButton>
      )}
    </li>
  )
}
